import Layout from "../../components/Layout";
import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import useSWR from "swr";
import Fuse from "fuse.js";
import { useDebouncedCallback } from "use-debounce";
import Head from "next/head";

import api, { fetchTenantConfig } from "../../services/api";
import { useAuth } from "../../hooks/useAuth";
import { DETAILS, CREDITS } from "../../components/FilmDetailTable";

import MediaGrid from "../../components/MediaGrid";
import NoContentPlaceholder from "../../components/NoContentPlaceholder";

export default function Search() {
  const router = useRouter();
  const rawQuery = new URLSearchParams(router.asPath.split("?")[1]).get("q");
  const [query, setQuery] = useState(rawQuery || "");
  const [results, setResults] = useState([]);

  const { tenant, apiLoaded, colorPalette, token } = useAuth();

  const {
    data: searchResponse,
    mutate,
    error
  } = useSWR(
    apiLoaded &&
      `watch/search?event_bucket=${tenant.event_bucket}${
        tenant?.sundance_login && token ? `&token=${token}` : ""
      }`,
    k => api.get(k).then(r => r.data)
  );

  useEffect(() => {
    if (apiLoaded && tenant?.sundance_login) {
      setTimeout(() => mutate(), 100);
    }
  }, [apiLoaded]);

  const [updateQueryUrl] = useDebouncedCallback(query => {
    router.replace(
      "/[festivalId]/search",
      `/${router.query.festivalId}/search${query ? `?q=${query}` : ""}`,
      {
        shallow: true
      }
    );
  }, 500);

  useEffect(() => {
    let data = (searchResponse?.results || [])
      .map(r => ({
        ...r,
        name: r.film_name || r.event_name,
        id: r.film_id || r.event_id
      }))
      .sort((a, b) => {
        // Sort "Film" before "Event", so that Film metadata is used in searching when filtering below.
        return -a.type.localeCompare(b.type);
      });

    // Remove duplicate exact name matches at the same time (for example, a screening containing
    // a film - don't show both in the result), bias towards first in list
    data = data.filter(
      (r, i) =>
        data.findIndex(
          x => x.name === r.name && x.start_time === r.start_time
        ) === i
    );

    const fuse = new Fuse(data, {
      shouldSort: true,
      threshold: 0.6,
      location: 0,
      distance: 1000,
      maxPatternLength: 32,
      minMatchCharLength: 2,
      includeMatches: true,
      keys: [
        { name: "name", weight: 0.7 },
        { name: "short_description", weight: 0.3 }
      ].concat(
        Object.keys(DETAILS)
          .concat(Object.keys(CREDITS))
          .map(k => {
            return {
              name: `${DETAILS[k] ? "details" : "credits"}.${k}`,
              weight: 0.1
            };
          })
      )
    });
    setResults(
      fuse
        .search(query.trim())
        .slice(0, 20)
        .map(r => {
          r.item._search_match_details = r.matches;
          return r.item;
        })
    );
    if (query.trim() !== (rawQuery || "")) {
      updateQueryUrl(query.trim());
    }
  }, [query, searchResponse]);

  return (
    <Layout>
      <Head>
        <title>Search | {tenant.virtual_festival_display_name}</title>
      </Head>
      <div className="relative rounded-md shadow-sm">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-7 w-7 sm:h-10 sm:w-10 text-gray-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8 4C5.79086 4 4 5.79086 4 8C4 10.2091 5.79086 12 8 12C10.2091 12 12 10.2091 12 8C12 5.79086 10.2091 4 8 4ZM2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 9.29583 13.5892 10.4957 12.8907 11.4765L17.7071 16.2929C18.0976 16.6834 18.0976 17.3166 17.7071 17.7071C17.3166 18.0976 16.6834 18.0976 16.2929 17.7071L11.4765 12.8907C10.4957 13.5892 9.29583 14 8 14C4.68629 14 2 11.3137 2 8Z"
            />
          </svg>
        </div>
        <input
          id="search"
          className="form-input block w-full pl-12 sm:pl-15 text-xl sm:text-3xl bg-gray-700 border-gray-500 focus:shadow-outline-gray focus:border-gray-400 text-white"
          placeholder="Type to search for anything..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      {!query || !results.length ? (
        <NoContentPlaceholder
          text="Start searching & who knows what you'll find"
          emoji="🎬"
        />
      ) : (
        <div className="mt-8">
          <MediaGrid
            festivalId={router.query.festivalId}
            items={results}
            colorPalette={colorPalette}
          />
        </div>
      )}
    </Layout>
  );
}

export async function getServerSideProps({ query, res, locale }) {
  if (res) {
    res.setHeader("Cache-Control", "s-maxage=1, stale-while-revalidate");
  }

  const tenant = await fetchTenantConfig(query.festivalId);
  return {
    props: {
      initialTenant: tenant
    }
  };
}
